import React, { PureComponent } from 'react';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import UnauthedHeader from '../../components/UnauthedHeader';
import { Button } from 'Common/components/Atoms/Button';
import { Card } from 'Common/components/Atoms/Card';
import { Div } from 'Common/components/Atoms/Div';
import { Flex } from 'Common/components/Atoms/Flex';
import { FontIcon } from 'Common/components/Atoms/FontIcon';
import { InputField } from 'Common/components/Atoms/InputField';
import { Link } from 'Common/components/Atoms/Link';
import { Loading } from 'Common/components/Atoms/Loading';
import { Toast } from 'Common/components/Molecules/Toast';
import { Text } from 'Common/components/Atoms/Text';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import { actions as loginActions } from '../../reducers/login';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { State as AppState } from '../../reducers/State';

export interface Props {
  requestError?: string;
  isLoading?: boolean;
  requestResetEmail: (email: string) => void;
  theme: Theme;
}

interface State {
  error?: string;
  email?: string;
  showToast: boolean;
}

export class ForgotPassword extends PureComponent<Props, State> {
  state: State = {
    email: '',
    showToast: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { requestError, isLoading } = this.props;
    if (!isLoading && prevProps.isLoading && !requestError) {
      this.setState({ showToast: true });
    }
  }

  handleChange = (value: string) => {
    this.setState({
      email: value,
      error: undefined,
    });
  };

  onKeyUpHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.submit();
    }
  };

  submit = () => {
    const { requestResetEmail } = this.props;
    const { email } = this.state;
    if (!email) {
      this.setState({ error: 'Please enter a valid email address.' });
    } else {
      requestResetEmail(email);
    }
  };

  render(): JSX.Element {
    const { theme, isLoading } = this.props;
    const { email, error, showToast } = this.state;
    return (
      <Div
        background={theme.palette.background.default}
        height="100vh"
        width="100vw"
        style={{ overflowY: 'auto' }}
      >
        <UnauthedHeader />
        <Text variant="h1" textAlign="center" mt={10} mb={4}>
          Password Reset
        </Text>
        <Card display="block" width={400} m="auto" py={4} px={5} mb={6}>
          <Text variant="body1" mb={3}>
            Enter your email and we'll send you a link to reset your password.
          </Text>
          <InputField
            id="email"
            fullWidth
            type="email"
            placeholder="Email address"
            value={email}
            startIcon={
              <FontIcon
                icon={faEnvelope}
                size="lg"
                color={theme.palette.grey[500]}
              />
            }
            onChange={(event) => this.handleChange(event.target.value)}
            error={!!error}
            helperText={!!error && error}
            onKeyUp={this.onKeyUpHandler}
          />
          <Button
            mt={3}
            variant="contained"
            color="primary"
            onClick={this.submit}
            width="100%"
            disabled={!email}
          >
            {isLoading ? (
              <Flex justifyContent="center" alignItems="center">
                <Loading size={24} customcolor={theme.palette.common.white} />
              </Flex>
            ) : (
              `Request Reset`
            )}
          </Button>
          <Div mt={3} pt={3} borderTop={`1px solid ${theme.palette.grey[300]}`}>
            <Text
              variant="body1"
              color="textSecondary"
              textAlign="center"
              fontWeight={theme.typography.fontWeightLight}
            >
              Remembered your password?{' '}
              <Link href="/login" color="secondary">
                Sign in
              </Link>
            </Text>
          </Div>
        </Card>
        <Toast
          severity="success"
          showToast={showToast}
          handleClose={() => this.setState({ showToast: false })}
          message={`Check your email -- We just sent a password reset link to ${email}.`}
          horizontal="center"
          vertical="bottom"
          icon={faEnvelope}
        />
      </Div>
    );
  }
}

function mapStateToProps(state: AppState, ownProps: any) {
  const { login } = state;
  return {
    isLoading: login?.isLoading,
    requestError: login?.error,
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators(loginActions, dispatch);
}

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(ForgotPassword)
);
